// Master toggle (per-dispositivo, durable) para activar la impresión Web Bluetooth.
//
// Vive en localStorage (sobrevive login/logout/recarga, igual que el perfil de impresoras
// `webBlePrintersConfig`) y se expone reactivo vía `useSyncExternalStore`, para que el
// toggle de Ajustes y el camino de impresión (Orders) reaccionen al cambio sin recargar.
//
// OFF (default) → opt-in: se imprime como siempre por el diálogo del navegador (pdfMake).
// ON            → si hay una impresora BLE habilitada y emparejada, se imprime directo.

import { useSyncExternalStore } from 'react';

const KEY = 'webBlePrintModeEnabled';
const listeners = new Set<() => void>();
let cached: boolean | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) {
      cached = event.newValue === '1';
      listeners.forEach((l) => l());
    }
  });
}

const read = (): boolean => {
  if (cached !== null) return cached;
  try {
    cached = typeof window !== 'undefined' && window.localStorage?.getItem(KEY) === '1';
  } catch {
    cached = false;
  }
  return cached;
};

export const getWebBlePrintMode = (): boolean => read();

export const setWebBlePrintMode = (enabled: boolean): void => {
  cached = enabled;
  try {
    if (typeof window !== 'undefined') window.localStorage?.setItem(KEY, enabled ? '1' : '0');
  } catch {
    /* localStorage no disponible — no-op */
  }
  listeners.forEach((l) => l());
};

export const subscribeWebBlePrintMode = (cb: () => void): (() => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

// Hook reactivo: `{ enabled, setEnabled }` del master toggle de impresión BLE.
export const useWebBlePrintMode = (): { enabled: boolean; setEnabled: (v: boolean) => void } => {
  const enabled = useSyncExternalStore(subscribeWebBlePrintMode, getWebBlePrintMode, () => false);
  return { enabled, setEnabled: setWebBlePrintMode };
};
